import React, { useEffect, useState } from 'react';
import {
    ai_generate_brand_watchlist_defaults,
    create_brand_watchlist_entry,
    load_brand_watchlist,
} from '../../services';
import './AIOnboardingCard.scss';

/**
 * AI-First Onboarding Card — per memory feedback_ai_first_ux.md.
 *
 * Defaults to a NOVA summary card ("here is what we will monitor"), not a
 * blank form. User can accept the whole proposal, customise per section,
 * or expand into the legacy form view.
 *
 * Shows itself only when the org has zero or near-zero watchlist entries.
 * Once the watchlist has real data, the card stays out of the way unless
 * the user explicitly opens it via a "Tune watchlist with NOVA" button.
 *
 * RFP items addressed:
 *  - "Reporting suitability for donor-facing communications" (the summary
 *    framing speaks the same language a donor or board member uses)
 *  - "Alert contextualization" — the per-section accept/customise gives
 *    the user explicit control over what gets flagged.
 */

const SECTION_SPECS = [
    { type: 'executive', title: 'Executives', desc: 'Senior leaders we will track across social, email-spoof, OSINT, and dark-web sources.' },
    { type: 'brand_handle', title: 'Brand Handles', desc: 'Official social-media handles on X, LinkedIn, Bluesky, Reddit, Telegram.' },
    { type: 'partner_domain', title: 'Approved Partners', desc: 'Domains authorised to mention partnership / funding relationships with you.' },
    { type: 'claim_phrase', title: 'Claim Phrases', desc: 'Search phrases used to detect unauthorised partnership or funding claims.' },
    { type: 'programme', title: 'Programmes', desc: 'Programme names so we can monitor mentions of your initiatives, not just your brand.' },
    { type: 'risk_tagged_source', title: 'Risk-Tagged Sources', desc: 'High-reach voices already pre-classified as antivax / conspiracy / disinformation.' },
    { type: 'event', title: 'Events', desc: 'Upcoming public appearances we will cross-reference against dark-web chatter.' },
];

function summariseEntry(type, v) {
    if (!v || typeof v !== 'object') return '—';
    switch (type) {
        case 'executive':       return `${v.name || ''}${v.role ? ` — ${v.role}` : ''}`;
        case 'brand_handle':    return v.canonical_handle || '';
        case 'partner_domain':  return v.domain || '';
        case 'claim_phrase':    return v.phrase || '';
        case 'programme':       return v.name || '';
        case 'risk_tagged_source': return `${v.platform || '?'}: ${v.handle || '?'}`;
        case 'event':           return `${v.name || ''}${v.venue ? ` @ ${v.venue}` : ''}`;
        default: return JSON.stringify(v);
    }
}

function uid() { return `${Date.now()}-${Math.random().toString(36).slice(2)}`; }


function AIOnboardingCard({ onApplied, forceOpen = false }) {
    const [proposal, setProposal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [existingCount, setExistingCount] = useState(null);
    const [applying, setApplying] = useState(false);
    const [appliedSummary, setAppliedSummary] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [existResp, propResp] = await Promise.all([
                    load_brand_watchlist({}),
                    ai_generate_brand_watchlist_defaults({}),
                ]);
                if (!alive) return;
                const existing = existResp?.statusCode?.data
                    ?? existResp?.statusCode?.response_data?.data
                    ?? existResp?.data
                    ?? [];
                setExistingCount(Array.isArray(existing) ? existing.length : 0);
                const body = propResp?.statusCode?.response_data?.data
                    ?? propResp?.statusCode
                    ?? propResp?.data
                    ?? propResp
                    ?? {};
                setProposal(body);
            } catch (e) {
                if (alive) setError(e?.message || 'Failed to load NOVA proposal');
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const applyProposal = async () => {
        if (!proposal || !proposal.proposed) return;
        setApplying(true);
        const summary = {};
        for (const [type, rows] of Object.entries(proposal.proposed)) {
            if (!Array.isArray(rows) || rows.length === 0) continue;
            summary[type] = 0;
            for (const value of rows) {
                if (!value) continue;
                try {
                    await create_brand_watchlist_entry(
                        { entry_type: type, value, is_active: true },
                        uid(),
                    );
                    summary[type] += 1;
                } catch (_) { /* dup or invalid — skip */ }
            }
        }
        setAppliedSummary(summary);
        setApplying(false);
        if (typeof onApplied === 'function') onApplied();
    };

    if (loading) {
        return (
            <div className='cepi_aionboard cepi_aionboard--loading'>
                <span className='cepi_aionboard__spin' /> NOVA is preparing your watchlist proposal…
            </div>
        );
    }
    if (error || !proposal) {
        return (
            <div className='cepi_aionboard cepi_aionboard--err'>
                Couldn’t generate the NOVA proposal: {error || 'no body returned'}
            </div>
        );
    }
    if (dismissed) return null;
    // Hide automatically once the user has a populated watchlist — unless the
    // caller explicitly summons it (forceOpen) to re-run NOVA AI suggestions.
    if (!forceOpen && existingCount != null && existingCount >= 7 && !appliedSummary) return null;

    const proposed = proposal.proposed || {};
    const totalProposed = Object.values(proposed).reduce(
        (a, rows) => a + (Array.isArray(rows) ? rows.length : 0), 0,
    );

    return (
        <div className='cepi_aionboard'>
            <div className='cepi_aionboard__head'>
                <div className='cepi_aionboard__icon'>★</div>
                <div className='cepi_aionboard__title-block'>
                    <div className='cepi_aionboard__eyebrow'>NOVA AI · Brand protection setup</div>
                    <h3 className='cepi_aionboard__title'>
                        Here is what we will monitor for {proposal.org_name || 'your organisation'}
                    </h3>
                    <p className='cepi_aionboard__sub'>
                        Industry template: <code>{proposal.industry_template || 'baseline'}</code> ·
                        {existingCount != null && ` ${existingCount} existing entries — `}
                        {totalProposed} new proposed
                    </p>
                </div>
                <button className='cepi_aionboard__dismiss' onClick={() => setDismissed(true)} title='Hide for now'>
                    ×
                </button>
            </div>

            {appliedSummary ? (
                <div className='cepi_aionboard__success'>
                    Applied: {Object.entries(appliedSummary).map(([t, n]) => `${n} ${t.replace(/_/g, ' ')}`).join(' · ')}.
                    Refresh the watchlist below to see them.
                </div>
            ) : (
                <>
                    <div className='cepi_aionboard__sections'>
                        {SECTION_SPECS.map((spec) => {
                            const rows = proposed[spec.type] || [];
                            return (
                                <div key={spec.type} className={`cepi_aionboard__section ${rows.length === 0 ? 'is-empty' : ''}`}>
                                    <div className='cepi_aionboard__section-head'>
                                        <span className='cepi_aionboard__section-title'>{spec.title}</span>
                                        <span className='cepi_aionboard__section-count'>{rows.length} proposed</span>
                                    </div>
                                    <p className='cepi_aionboard__section-desc'>{spec.desc}</p>
                                    {rows.length > 0 && (
                                        <ul className='cepi_aionboard__section-list'>
                                            {rows.slice(0, 5).map((r, i) => (
                                                <li key={i}>{summariseEntry(spec.type, r)}</li>
                                            ))}
                                            {rows.length > 5 && <li className='is-more'>…and {rows.length - 5} more</li>}
                                        </ul>
                                    )}
                                    {rows.length === 0 && (
                                        <p className='cepi_aionboard__section-empty'>
                                            NOVA doesn’t have suggestions yet for this section — add manually below.
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className='cepi_aionboard__cta'>
                        <button className='cepi_aionboard__btn cepi_aionboard__btn--primary'
                            onClick={applyProposal} disabled={applying || totalProposed === 0}>
                            {applying ? 'Applying…' : `✓ Looks good — apply ${totalProposed} entries`}
                        </button>
                        <button className='cepi_aionboard__btn' onClick={() => setDismissed(true)}>
                            Customise everything
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export default AIOnboardingCard;
